import React, { useEffect, useMemo, useState } from 'react';

const API_DATE_INPUT_FORMAT = (date) => {
  if (!date) return '';
  const d = new Date(date);
  if (Number.isNaN(d.getTime())) return '';
  return d.toISOString().slice(0, 10);
};

const formatDisplayDate = (value) => {
  if (!value) return '-';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleDateString();
};

function CandidateWorkDiary({
  apiUrl,
  diary,
  status,
  loading,
  onToast,
  onRefresh,
  candidateName
}) {
  const [details, setDetails] = useState({
    establishment_name: '',
    establishment_address: '',
    apprenticeship_code: '',
    trade: ''
  });
  const [entries, setEntries] = useState([]);
  const [detailsError, setDetailsError] = useState('');
  const [entryError, setEntryError] = useState('');
  const [savingDetails, setSavingDetails] = useState(false);
  const [savingEntry, setSavingEntry] = useState(false);
  const [entryForm, setEntryForm] = useState({
    week_start: '',
    week_end: '',
    job_work_done: '',
    supervisor_signature: ''
  });
  const [editingEntryId, setEditingEntryId] = useState(null);

  useEffect(() => {
    if (diary) {
      // Prefer actual saved diary values; fall back to suggested details if present
      const suggested = diary._suggestedDetails || {};
      setDetails({
        establishment_name: diary.establishment_name || suggested.establishment_name || '',
        establishment_address: diary.establishment_address || suggested.establishment_address || '',
        apprenticeship_code: diary.apprenticeship_code || suggested.apprenticeship_code || '',
        trade: diary.trade || suggested.trade || ''
      });
      setEntries((diary.entries || []).slice().sort((a, b) => {
        if (a.serial_number === b.serial_number) {
          return new Date(a.week_start) - new Date(b.week_start);
        }
        return a.serial_number - b.serial_number;
      }));
    } else {
      setDetails({
        establishment_name: '',
        establishment_address: '',
        apprenticeship_code: '',
        trade: ''
      });
      setEntries([]);
    }
  }, [diary]);

  const nextSerialNumber = useMemo(() => {
    if (!entries.length) return 1;
    return Math.max(...entries.map((e) => Number(e.serial_number) || 0)) + 1;
  }, [entries]);

  const handleDetailsChange = (e) => {
    const { name, value } = e.target;
    setDetails((prev) => ({ ...prev, [name]: value }));
  };

  const handleEntryChange = (e) => {
    const { name, value } = e.target;
    setEntryForm((prev) => ({ ...prev, [name]: value }));
  };

  const ensureToken = () => {
    const token = localStorage.getItem('gogig_token');
    if (!token) {
      alert('Session expired. Please log in again.');
      window.location.href = '/welcome';
      return null;
    }
    return token;
  };

  const handleSaveDetails = async (e) => {
    e.preventDefault();
    setDetailsError('');
    const token = ensureToken();
    if (!token) return;

    if (!details.establishment_name || !details.establishment_address || !details.apprenticeship_code || !details.trade) {
      setDetailsError('All fields are required.');
      return;
    }

    setSavingDetails(true);
    try {
      const res = await fetch(`${apiUrl}/api/candidate/work-diary`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(details)
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data.error || 'Failed to save work diary');
      }
      onToast && onToast('Work diary details saved');
      await onRefresh();
    } catch (err) {
      setDetailsError(err.message);
    } finally {
      setSavingDetails(false);
    }
  };

  const handleSubmitEntry = async (e) => {
    e.preventDefault();
    setEntryError('');
    const token = ensureToken();
    if (!token) return;

    if (!details.establishment_name || !details.apprenticeship_code) {
      setEntryError('Save the work diary details before adding entries.');
      return;
    }

    if (!entryForm.week_start || !entryForm.week_end || !entryForm.job_work_done.trim()) {
      setEntryError('Week range and job details are required.');
      return;
    }

    setSavingEntry(true);
    try {
      const isEditing = !!editingEntryId;
      const endpoint = isEditing
        ? `${apiUrl}/api/candidate/work-diary/entries/${editingEntryId}`
        : `${apiUrl}/api/candidate/work-diary/entries`;
      const method = isEditing ? 'PATCH' : 'POST';
      const res = await fetch(endpoint, {
        method,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          ...entryForm,
          job_work_done: entryForm.job_work_done.trim()
        })
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data.error || (isEditing ? 'Failed to update entry' : 'Failed to add entry'));
      }
      onToast && onToast(isEditing ? 'Work diary entry updated' : 'Work diary entry added');
      setEntryForm({
        week_start: '',
        week_end: '',
        job_work_done: '',
        supervisor_signature: ''
      });
      setEditingEntryId(null);
      await onRefresh();
    } catch (err) {
      setEntryError(err.message);
    } finally {
      setSavingEntry(false);
    }
  };

  const handleEditClick = (entry) => {
    setEditingEntryId(entry.id);
    setEntryForm({
      week_start: entry.week_start,
      week_end: entry.week_end,
      job_work_done: entry.job_work_done || '',
      supervisor_signature: entry.supervisor_signature || ''
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDeleteEntry = async (entryId) => {
    if (!window.confirm('Delete this entry? This action cannot be undone.')) return;
    const token = ensureToken();
    if (!token) return;
    try {
      const res = await fetch(`${apiUrl}/api/candidate/work-diary/entries/${entryId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || (data.success === false)) {
        throw new Error(data.error || 'Failed to delete entry');
      }
      onToast && onToast('Entry deleted');
      if (editingEntryId === entryId) {
        setEditingEntryId(null);
        setEntryForm({
          week_start: '',
          week_end: '',
          job_work_done: '',
          supervisor_signature: ''
        });
      }
      await onRefresh();
    } catch (err) {
      setEntryError(err.message);
    }
  };

  return (
    <div style={{ textAlign: 'left' }}>
      <div style={{
        background: '#f0fdf4',
        border: '1px solid #bbf7d0',
        borderRadius: 16,
        padding: 20,
        marginBottom: 24
      }}>
        <h2 style={{ margin: 0, fontSize: 22, color: '#166534' }}>Work Diary Tracker</h2>
        <p style={{ marginTop: 8, marginBottom: 0, color: '#166534' }}>
          Keep your weekly apprenticeship activities updated. The diary mirrors the Apprentices Act Format 2.
        </p>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginTop: 16 }}>
          <div style={{ flex: '1 1 160px', background: '#fff', borderRadius: 12, padding: 12, border: '1px solid #bbf7d0' }}>
            <div style={{ fontSize: 12, color: '#16a34a', fontWeight: 600 }}>Last Entry</div>
            <div style={{ fontSize: 16, fontWeight: 700, color: '#14532d' }}>
              {status?.lastEntryDate ? formatDisplayDate(status.lastEntryDate) : 'Not submitted'}
            </div>
          </div>
          <div style={{ flex: '1 1 160px', background: '#fff', borderRadius: 12, padding: 12, border: '1px solid #bbf7d0' }}>
            <div style={{ fontSize: 12, color: '#16a34a', fontWeight: 600 }}>Next Due</div>
            <div style={{ fontSize: 16, fontWeight: 700, color: '#14532d' }}>
              {status?.nextDueDate ? formatDisplayDate(status.nextDueDate) : 'Whenever ready'}
            </div>
          </div>
          <div style={{ flex: '1 1 160px', background: status?.reminderDue ? '#fee2e2' : '#ecfdf5', borderRadius: 12, padding: 12, border: `1px solid ${status?.reminderDue ? '#fca5a5' : '#bbf7d0'}` }}>
            <div style={{ fontSize: 12, color: status?.reminderDue ? '#b91c1c' : '#16a34a', fontWeight: 600 }}>Reminder</div>
            <div style={{ fontSize: 16, fontWeight: 700, color: status?.reminderDue ? '#991b1b' : '#14532d' }}>
              {status?.reminderDue ? 'Update due this week' : 'All caught up'}
            </div>
          </div>
        </div>
      </div>

      <form onSubmit={handleSaveDetails} style={{
        background: '#fff',
        borderRadius: 16,
        border: '1px solid #e5e7eb',
        padding: 24,
        marginBottom: 24,
        boxShadow: '0 2px 12px rgba(22, 163, 74, 0.08)'
      }}>
        <h3 style={{ marginTop: 0, color: '#166534', fontSize: 20 }}>Establishment Details</h3>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 16 }}>
          <div>
            <label style={{ fontWeight: 600, color: '#166534', display: 'block', marginBottom: 6 }}>Name of Establishment</label>
            <input
              type="text"
              name="establishment_name"
              value={details.establishment_name}
              onChange={handleDetailsChange}
              className="gogig-input"
              placeholder="Enter establishment name"
              required
            />
          </div>
          <div>
            <label style={{ fontWeight: 600, color: '#166534', display: 'block', marginBottom: 6 }}>Address of Establishment</label>
            <textarea
              name="establishment_address"
              value={details.establishment_address}
              onChange={handleDetailsChange}
              className="gogig-input"
              style={{ minHeight: 80 }}
              placeholder="Enter full address"
              required
            />
          </div>
          <div>
            <label style={{ fontWeight: 600, color: '#166534', display: 'block', marginBottom: 6 }}>Name of Apprentice</label>
            <input
              type="text"
              value={candidateName || ''}
              readOnly
              className="gogig-input"
              style={{ background: '#f1f5f9', cursor: 'not-allowed' }}
            />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16 }}>
            <div>
              <label style={{ fontWeight: 600, color: '#166534', display: 'block', marginBottom: 6 }}>Apprenticeship Code</label>
              <input
                type="text"
                name="apprenticeship_code"
                value={details.apprenticeship_code}
                onChange={handleDetailsChange}
                className="gogig-input"
                placeholder="Enter code"
                required
              />
            </div>
            <div>
              <label style={{ fontWeight: 600, color: '#166534', display: 'block', marginBottom: 6 }}>Trade</label>
              <input
                type="text"
                name="trade"
                value={details.trade}
                onChange={handleDetailsChange}
                className="gogig-input"
                placeholder="Enter trade"
                required
              />
            </div>
          </div>
        </div>
        {detailsError && <div style={{ color: '#b91c1c', marginTop: 12 }}>{detailsError}</div>}
        <button
          type="submit"
          className="gogig-card-btn"
          style={{ marginTop: 16, alignSelf: 'flex-start' }}
          disabled={savingDetails}
        >
          {savingDetails ? 'Saving...' : 'Save Details'}
        </button>
      </form>

      <form onSubmit={handleSubmitEntry} style={{
        background: '#fff',
        borderRadius: 16,
        border: '1px solid #e5e7eb',
        padding: 24,
        marginBottom: 24,
        boxShadow: '0 2px 12px rgba(22, 163, 74, 0.08)'
      }}>
        <h3 style={{ marginTop: 0, color: '#166534', fontSize: 20 }}>
          {editingEntryId ? 'Edit Weekly Entry' : 'Add Weekly Entry'}
        </h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 16 }}>
          <div>
            <label style={{ fontWeight: 600, color: '#166534', display: 'block', marginBottom: 6 }}>Week Start</label>
            <input
              type="date"
              name="week_start"
              value={API_DATE_INPUT_FORMAT(entryForm.week_start)}
              onChange={handleEntryChange}
              className="gogig-input"
              required
            />
          </div>
          <div>
            <label style={{ fontWeight: 600, color: '#166534', display: 'block', marginBottom: 6 }}>Week End</label>
            <input
              type="date"
              name="week_end"
              value={API_DATE_INPUT_FORMAT(entryForm.week_end)}
              onChange={handleEntryChange}
              className="gogig-input"
              required
            />
          </div>
        </div>
        <div style={{ marginTop: 16 }}>
          <label style={{ fontWeight: 600, color: '#166534', display: 'block', marginBottom: 6 }}>Job / Work done during the week</label>
          <textarea
            name="job_work_done"
            value={entryForm.job_work_done}
            onChange={handleEntryChange}
            className="gogig-input"
            style={{ minHeight: 100 }}
            placeholder="Describe the key activities and skills practiced..."
            required
          />
        </div>
        {/* Supervisor signature is managed by supervisors/admins, not candidates */}
        {!editingEntryId && (
          <div style={{ marginTop: 16, fontSize: 13, color: '#475569' }}>
            Serial number will auto-increment. Next entry will be #{nextSerialNumber}.
          </div>
        )}
        {entryError && <div style={{ color: '#b91c1c', marginTop: 12 }}>{entryError}</div>}
        <button
          type="submit"
          className="gogig-card-btn"
          style={{ marginTop: 16 }}
          disabled={savingEntry}
        >
          {savingEntry ? (editingEntryId ? 'Saving...' : 'Adding...') : (editingEntryId ? 'Save Changes' : 'Add Entry')}
        </button>
        {editingEntryId && (
          <button
            type="button"
            className="gogig-card-btn-outline"
            style={{ marginTop: 16, marginLeft: 8 }}
            onClick={() => {
              setEditingEntryId(null);
              setEntryForm({
                week_start: '',
                week_end: '',
                job_work_done: '',
                supervisor_signature: ''
              });
              setEntryError('');
            }}
          >
            Cancel
          </button>
        )}
      </form>

      <div style={{
        background: '#fff',
        borderRadius: 16,
        border: '1px solid #e5e7eb',
        padding: 24,
        boxShadow: '0 2px 12px rgba(22, 163, 74, 0.06)'
      }}>
        <h3 style={{ marginTop: 0, color: '#166534', fontSize: 20 }}>Work Diary Entries</h3>
        {loading ? (
          <div style={{ padding: 24, textAlign: 'center', color: '#64748b' }}>Loading work diary...</div>
        ) : entries.length === 0 ? (
          <div style={{ padding: 24, textAlign: 'center', color: '#64748b' }}>
            No entries yet. Start by adding your first weekly summary above.
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 600 }}>
              <thead>
                <tr style={{ background: '#ecfdf5', color: '#065f46' }}>
                  <th style={{ padding: '12px 10px', textAlign: 'left', borderBottom: '1px solid #d1fae5' }}>Serial</th>
                  <th style={{ padding: '12px 10px', textAlign: 'left', borderBottom: '1px solid #d1fae5' }}>Week</th>
                  <th style={{ padding: '12px 10px', textAlign: 'left', borderBottom: '1px solid #d1fae5' }}>Job / Work Done</th>
                  <th style={{ padding: '12px 10px', textAlign: 'left', borderBottom: '1px solid #d1fae5' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {entries.map((entry) => (
                  <tr key={entry.id} style={{ borderBottom: '1px solid #e2e8f0' }}>
                    <td style={{ padding: '12px 10px', fontWeight: 600 }}>{entry.serial_number}</td>
                    <td style={{ padding: '12px 10px' }}>
                      {formatDisplayDate(entry.week_start)} – {formatDisplayDate(entry.week_end)}
                    </td>
                    <td style={{ padding: '12px 10px', whiteSpace: 'pre-wrap' }}>{entry.job_work_done}</td>
                    <td style={{ padding: '12px 10px', display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                      <button
                        type="button"
                        className="gogig-card-btn-outline"
                        onClick={() => handleEditClick(entry)}
                      >
                        Edit
                      </button>
                      <button
                        type="button"
                        className="gogig-card-btn-outline"
                        onClick={() => handleDeleteEntry(entry.id)}
                        style={{ color: '#b91c1c', borderColor: '#fecaca' }}
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <div style={{ marginTop: 16, fontSize: 12, color: '#475569' }}>
          Need a PDF copy? Ask the admin team to export it from the dashboard.
        </div>
      </div>
    </div>
  );
}

CandidateWorkDiary.defaultProps = {
  diary: null,
  status: null,
  loading: false,
  onToast: null,
  onRefresh: async () => {},
  candidateName: ''
};

export default CandidateWorkDiary;



